import { useMemo } from 'react';

const WHATSAPP_PHONES: Record<string, string> = {
    UY: '59897134559',
    CL: '56981306930',
    CO: '573173637660',
    PY: '59897134559',
    PA: '573173637660',
    DO: '573173637660',
};

interface Props {
    content: string;
    isStreaming?: boolean;
    countryCode?: string;
    projectTitle?: string;
}

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

/**
 * Renderiza una tabla markdown simple a HTML
 * Soporta: | col1 | col2 | col3 |
 */
function renderTable(tableLines: string[]): string {
    if (tableLines.length < 2) return tableLines.map(l => escapeHtml(l)).join('<br />');

    const parseRow = (line: string): string[] =>
        line.split('|').map(c => c.trim()).filter(Boolean);

    const headers = parseRow(tableLines[0]);

    // Skip separator row (| --- | --- |)
    const startIdx = tableLines[1].includes('---') ? 2 : 1;
    const bodyRows = tableLines.slice(startIdx).map(parseRow);

    let html = '<div class="chat-table-wrap"><table class="chat-table">';
    html += '<thead><tr>';
    headers.forEach(h => { html += `<th>${formatInline(escapeHtml(h))}</th>`; });
    html += '</tr></thead>';
    html += '<tbody>';
    bodyRows.forEach(row => {
        html += '<tr>';
        row.forEach(cell => { html += `<td>${formatInline(escapeHtml(cell))}</td>`; });
        html += '</tr>';
    });
    html += '</tbody></table></div>';

    return html;
}

type LineType = 'ul' | 'ol' | 'other';

function classifyLine(line: string): { type: LineType; content: string } {
    // Unordered list: - item or * item (but not ** which is bold)
    const ulMatch = line.match(/^(\s*)[-*] (.+)$/);
    if (ulMatch && !line.match(/^\s*\*\*.+\*\*/)) {
        return { type: 'ul', content: ulMatch[2] };
    }
    // Ordered list: 1. item
    const olMatch = line.match(/^(\s*)\d+\. (.+)$/);
    if (olMatch) {
        return { type: 'ol', content: olMatch[2] };
    }
    return { type: 'other', content: line };
}

function formatInline(text: string): string {
    return text
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
        .replace(/`([^`]+)`/g, '<code>$1</code>');
}

export const ChatMarkdown = ({ content, isStreaming, countryCode, projectTitle }: Props) => {
    const { rendered, whatsappQuery } = useMemo(() => {
        if (!content) return { rendered: '', whatsappQuery: '' };

        // Extract [WHATSAPP:query] tag
        let textContent = content;
        let query = '';
        const waMatch = content.match(/\[WHATSAPP:(.+?)\]/);
        if (waMatch) {
            query = waMatch[1].trim();
            textContent = content.replace(waMatch[0], '').trim();
        }

        const lines = textContent.split('\n');
        const result: string[] = [];
        let i = 0;

        const flushList = (listLines: string[], tag: 'ul' | 'ol') => {
            result.push(`<${tag}>`);
            listLines.forEach(item => {
                result.push(`<li>${formatInline(escapeHtml(item))}</li>`);
            });
            result.push(`</${tag}>`);
        };

        while (i < lines.length) {
            const line = lines[i];

            // Detectar tabla (linea que empieza con |)
            if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
                const tableLines: string[] = [];
                while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
                    tableLines.push(lines[i].trim());
                    i++;
                }
                result.push(renderTable(tableLines));
                continue;
            }

            // Detectar listas consecutivas
            const classified = classifyLine(line);
            if (classified.type === 'ul' || classified.type === 'ol') {
                const listType = classified.type;
                const listItems: string[] = [classified.content];
                i++;
                while (i < lines.length) {
                    const next = classifyLine(lines[i]);
                    if (next.type === listType) {
                        listItems.push(next.content);
                        i++;
                    } else {
                        break;
                    }
                }
                flushList(listItems, listType);
                continue;
            }

            // Procesar linea normal
            let html = escapeHtml(line);
            html = formatInline(html);

            if (html.trim() === '') {
                html = '<br />';
            } else {
                html = `<p>${html}</p>`;
            }

            result.push(html);
            i++;
        }

        return { rendered: result.join(''), whatsappQuery: query };
    }, [content]);

    const whatsappPhone = countryCode ? WHATSAPP_PHONES[countryCode] ?? WHATSAPP_PHONES.UY : WHATSAPP_PHONES.UY;
    const whatsappText = whatsappQuery || '';
    const whatsappUrl = whatsappText
        ? `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(whatsappText)}`
        : '';

    return (
        <div className="chat-md">
            <div dangerouslySetInnerHTML={{ __html: rendered }} />
            {whatsappUrl && !isStreaming && (
                <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="chat-whatsapp-btn"
                >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="tw-w-4 tw-h-4 tw-shrink-0">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    Consultar por WhatsApp
                </a>
            )}
            {isStreaming && (
                <span className="tw-inline-block tw-w-1.5 tw-h-3.5 tw-bg-primary tw-ml-0.5 chat-cursor" />
            )}
        </div>
    );
};

export default ChatMarkdown;
